/**
 * Tenant Feature Management Component
 * Allows super admins to enable/disable features for tenants
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { 
  ToggleLeft,
  ToggleRight,
  Loader2,
  Save,
  BookOpen,
  Beaker,
  Route,
  Video,
  BookMarked,
  Building2,
  Award,
  Bot,
  Sparkles,
  ClipboardCheck,
  GraduationCap,
  Bell,
  Megaphone,
  Trophy,
  Globe,
  Heart,
  BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
const API = API_BASE;

// Feature definitions with icons and descriptions
const FEATURE_DEFINITIONS = [
  { 
    key: 'courses', 
    label: 'Courses', 
    icon: BookOpen, 
    description: 'Access to course catalog and learning content',
    required: true  // Cannot be disabled
  },
  { 
    key: 'learning_labs', 
    label: 'Learning Labs', 
    icon: Beaker, 
    description: 'Interactive workplace scenario simulations' 
  },
  { 
    key: 'learning_pathways', 
    label: 'Learning Pathways', 
    icon: Route, 
    description: 'Curated learning paths and progression tracks' 
  },
  { 
    key: 'masterclasses', 
    label: 'Masterclasses', 
    icon: Video, 
    description: 'Live and recorded webinar sessions' 
  },
  { 
    key: 'ebooks', 
    label: 'eBooks', 
    icon: BookMarked, 
    description: 'Digital book library and resources' 
  },
  { 
    key: 'wsp', 
    label: 'WSP Analytics', 
    icon: Building2, 
    description: 'Workplace Skills Plan reporting and analysis' 
  },
  { 
    key: 'certificates', 
    label: 'Certificates', 
    icon: Award, 
    description: 'Course completion certificates and credentials' 
  },
  { 
    key: 'navi_coach', 
    label: 'NAVI AI Coach', 
    icon: Bot, 
    description: 'AI-powered coaching and grading assistant' 
  },
  { 
    key: 'ai_course_generation', 
    label: 'AI Course Generation', 
    icon: Sparkles, 
    description: 'Create courses using AI (Master only recommended)',
    masterOnly: true
  },
  { 
    key: 'cpd_tracking', 
    label: 'CPD Tracking', 
    icon: ClipboardCheck, 
    description: 'Continuous Professional Development tracking' 
  },
  {
    key: 'navigate_academy',
    label: 'Navigate Academy',
    icon: GraduationCap,
    description: 'ICA compliance course catalog and enrollment portal'
  },
  {
    key: 'regulatory_alerts',
    label: 'Regulatory Alerts',
    icon: Bell,
    description: 'Real-time regulatory news and compliance alerts'
  },
  {
    key: 'campaigns',
    label: 'Campaigns',
    icon: Megaphone,
    description: 'Learning campaigns and promotional content management'
  },
  {
    key: 'gamification',
    label: 'Gamification',
    icon: Trophy,
    description: 'Points, leaderboards, and achievement system'
  },
  {
    key: 'external_courses',
    label: 'External Courses',
    icon: Globe,
    description: 'SCORM/AICC external learning content integration'
  },
  {
    key: 'programmes',
    label: 'Professional Programmes',
    icon: GraduationCap,
    description: 'Structured learning programmes with POE, gradebook, and learner tracking'
  },
  {
    key: 'candidacy',
    label: 'Candidacy Portal',
    icon: Award,
    description: 'Professional designation readiness and candidacy management'
  },
  { 
    key: 'subscription', 
    label: 'Subscription Banners', 
    icon: ClipboardCheck, 
    description: 'Show subscription/trial expiry banners (disable for enterprise licenses)' 
  },
  { 
    key: 'badges', 
    label: 'Badges & Gamification', 
    icon: Award, 
    description: 'Achievement badges and gamified learning elements' 
  },
  { 
    key: 'role_profiling', 
    label: 'AI Role Profiling', 
    icon: Bot, 
    description: 'AI-powered role analysis and course recommendations' 
  },
  {
    key: 'wishlists',
    label: 'Wishlists',
    icon: Heart,
    description: 'Learners can save courses to a wishlist (wishlist data is kept when off)'
  },
  {
    key: 'leaderboard',
    label: 'Leaderboard',
    icon: BarChart3,
    description: 'Public XP leaderboard page'
  },
];

export default function TenantFeatureManagement({ token, organizationId, organizationName }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState({});
  const [originalFeatures, setOriginalFeatures] = useState({});

  useEffect(() => {
    fetchFeatures();
  }, [organizationId]);

  const fetchFeatures = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/api/organizations/${organizationId}/features`);
      setFeatures(res.data.features || {});
      setOriginalFeatures(res.data.features || {});
    } catch (error) {
      console.error('Failed to fetch features:', error);
      toast.error('Failed to load feature settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = (featureKey) => {
    setFeatures(prev => ({
      ...prev,
      [featureKey]: prev[featureKey] === false // missing key = enabled, so toggle turns it off
    }));
  };

  const hasChanges = () => {
    return JSON.stringify(features) !== JSON.stringify(originalFeatures);
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/api/organizations/${organizationId}/features`,
        features,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOriginalFeatures({ ...features });
      toast.success('Feature settings saved - applied immediately');
      // Live-refresh navigation/guards in this browser; other users pick it up on their next navigation
      window.dispatchEvent(new Event('features-updated'));
    } catch (error) {
      console.error('Failed to save features:', error);
      toast.error(error.response?.data?.detail || 'Failed to save features');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
        <p className="text-zinc-400 text-center">
          Select a tenant organization to manage features.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-white">
            Feature Management
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Configure which features are available for <span className="text-navigate-gold">{organizationName}</span>
          </p>
          <p className="text-xs text-zinc-500 mt-1" data-testid="sections-data-note">
            Turning a section off hides it from navigation, dashboards, search and direct links for learners. No data is deleted,
            and admins can still manage the content from this admin panel.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges()}
          data-testid="features-save-btn"
          className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Feature Toggles */}
      <div className="grid gap-4">
        {FEATURE_DEFINITIONS.map((feature) => {
          const Icon = feature.icon;
          const isEnabled = features[feature.key] !== false;
          const isRequired = feature.required;
          const isMasterOnly = feature.masterOnly;
          
          return (
            <div 
              key={feature.key}
              className={`p-4 rounded-xl border transition-all ${
                isEnabled 
                  ? 'bg-navigate-gold/10 border-navigate-gold/30' 
                  : 'bg-navigate-surface border-zinc-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${
                    isEnabled ? 'bg-navigate-gold/20' : 'bg-zinc-800'
                  }`}>
                    <Icon className={`h-5 w-5 ${
                      isEnabled ? 'text-navigate-gold' : 'text-zinc-500'
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium ${
                        isEnabled ? 'text-white' : 'text-zinc-400'
                      }`}>
                        {feature.label}
                      </h3>
                      {isRequired && (
                        <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                          Required
                        </span>
                      )}
                      {isMasterOnly && (
                        <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">
                          Master Only
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500 mt-0.5">
                      {feature.description}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => !isRequired && toggleFeature(feature.key)}
                  disabled={isRequired}
                  data-testid={`feature-toggle-${feature.key}`}
                  className={`transition-colors ${
                    isRequired ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  title={isRequired ? 'This feature cannot be disabled' : (isEnabled ? 'Disable' : 'Enable')}
                >
                  {isEnabled ? (
                    <ToggleRight className="h-8 w-8 text-navigate-gold" />
                  ) : (
                    <ToggleLeft className="h-8 w-8 text-zinc-600" />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Warning for disabled features */}
      {Object.values(features).some(v => v === false) && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <p className="text-sm text-amber-400">
            <strong>Note:</strong> Disabled features will be hidden from the navigation 
            and inaccessible to users in this tenant. Existing data is preserved.
          </p>
        </div>
      )}
    </div>
  );
}
